import type { ZoneShape } from "@gettactic/map-builder/src/types";
import { createZone } from "@gettactic/map-builder/src/utils/shapeFactory";
import { computeWallFloorPolygons } from "@gettactic/map-builder/src/utils/wallFloorFill";
import { getAllWallSegments } from "@gettactic/map-builder/src/utils/wallPathSegments";
import { normalizeZonePolygon } from "@gettactic/map-builder/src/utils/zonePolygon";
import { addShapes } from "../document/operations";
import {
  polygonArea,
  type RoomPolygon,
  unionRoomPolygons
} from "../document/roomPolygons";
import { orderedShapes } from "../document/serialize";
import type {
  AutoZonePick,
  EditorState,
  EditorStore
} from "../store/createEditorStore";

/** Rooms the wall graph encloses, in the order the overlay draws them. */
export const roomPolygonsOf = (state: EditorState): RoomPolygon[] =>
  computeWallFloorPolygons(getAllWallSegments(orderedShapes(state)));

/** Floor area the walls enclose, in square pixels: the detected rooms
 *  tile the inside of the outer walls, so their areas add up to it. */
export const buildingFootprint = (
  rooms: RoomPolygon[]
): { areaPx: number; rooms: number } | null => {
  if (rooms.length === 0) return null;
  return {
    areaPx: rooms.reduce((sum, room) => sum + polygonArea(room.points), 0),
    rooms: rooms.length
  };
};

const zoneCount = (state: EditorState): number =>
  state.order.filter((id) => state.shapes[id]?.type === "zone").length;

/** The name a pick gets when none was typed: its zone's name, numbered
 *  when several picks share the zone, else the next free "Room n". */
export const defaultPickName = (
  picks: AutoZonePick[],
  index: number,
  existingZones: number
): string => {
  const pick = picks[index];
  if (pick.tag) {
    const siblings = picks.filter((other) => other.tag?.id === pick.tag?.id);
    if (siblings.length === 1) return pick.tag.name;
    return `${pick.tag.name} ${siblings.indexOf(pick) + 1}`;
  }
  return `Room ${existingZones + index + 1}`;
};

/** The zones the picks would become: one polygon zone per pick, merged
 *  rooms as one outline, named and tagged as chosen in the card. */
export const zonesFromPicks = (
  state: EditorState,
  rooms: RoomPolygon[]
): ZoneShape[] => {
  const picks = state.chrome.autoZonePicks;
  const existing = zoneCount(state);
  const zones: ZoneShape[] = [];
  picks.forEach((pick, index) => {
    const picked = pick.rooms
      .map((room) => rooms[room])
      .filter((room): room is RoomPolygon => !!room);
    if (picked.length === 0) return;
    const outline = unionRoomPolygons(picked);
    if (!outline || outline.length < 3) return;
    const zone = {
      ...(createZone({ x: 0, y: 0 }) as ZoneShape),
      ...normalizeZonePolygon(
        { x: 0, y: 0 },
        outline,
        outline.map(() => ({}))
      ),
      name: pick.name?.trim() || defaultPickName(picks, index, existing)
    } as ZoneShape;
    if (pick.tag) {
      zone.zone_id = pick.tag.id;
      zone.categoryTypeId = pick.tag.id;
    }
    zones.push(zone);
  });
  return zones;
};

/** Creates the picked zones in one undo step, selects them and returns
 *  to the select tool. */
export const approveAutoZones = (store: EditorStore): void => {
  const state = store.getState();
  const zones = zonesFromPicks(state, roomPolygonsOf(state));
  if (zones.length > 0) {
    state.commit((document) => addShapes(document, zones), {
      selection: zones.map((zone) => zone.id)
    });
  }
  store.getState().setChrome({ autoZonePicks: [], zoneToolTag: null });
  store.getState().setUi({ showZones: true });
  store.getState().setTool("select");
};
